// File: src/PrivacyPolicy.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

const brand = {
  primary: "#B1336E",
  accentGold: "#E6B43C",
  accentGreen: "#67A43C",
};

const container = "mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8";
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" },
};

// Smooth scroll helper
const scrollTo = (id) => (e) => {
  e?.preventDefault?.();
  const targetId = (id || "").replace(/^#/, "");
  const el = document.getElementById(targetId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const base = window.location.hash.split("#")[1] || "";
    const basePath = base.includes("/") ? base.split("?")[0] : base;
    window.history.replaceState(null, "", `#${basePath}`);
  }
};

export default function PrivacyPolicy() {
  const toc = [
    ["Objeto y alcance", "objeto"],
    ["Identidad del responsable", "responsable"],
    ["Bases legales y finalidades", "bases-finalidades"],
    ["Datos que tratamos", "datos"],
    ["Fuentes de datos", "fuentes"],
    ["Mensajería (WhatsApp) y comunicaciones", "whatsapp"],
    ["Geolocalización y seguimiento", "geo"],
    ["Cookies y tecnologías similares", "cookies"],
    ["Conservación (plazos)", "plazos"],
    ["Encargados, destinatarios y transferencias", "encargados"],
    ["Transferencias internacionales", "transferencias"],
    ["Seguridad de la información", "seguridad"],
    ["Derechos ARCO y revocación", "arco"],
    ["Menores de edad", "menores"],
    ["Cambios a esta política", "cambios"],
    ["Contacto y canal de privacidad", "contacto"],
  ];

  return (
    <div className="bg-white text-slate-900">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className={`${container} flex h-16 items-center justify-between`}>
          <Link to="/" className="flex items-center gap-2" aria-label="FAST MAX">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="FAST MAX" className="h-8 w-auto" />
            <span className="hidden sm:inline text-lg font-extrabold tracking-wide" style={{ color: brand.primary }}>
              FAST MAX
            </span>
          </Link>

          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 sm:text-sm sm:px-4 sm:py-2"
          >
            Volver al inicio
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </header>

      {/* Body */}
      <main>
        <section className="bg-white">
          <div className={`${container} py-10 md:py-14`}>
            <motion.h1
              {...fadeUp}
              className="text-3xl md:text-4xl font-extrabold"
              style={{ color: brand.primary }}
            >
              Política de Privacidad
            </motion.h1>
            <motion.p {...fadeUp} transition={{ delay: 0.05 }} className="mt-2 text-slate-600">
              Última actualización: {new Date().toLocaleDateString("es-PE")}
            </motion.p>

            {/* Intro */}
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm"
            >
              <p>
                Esta Política describe cómo <strong>FAST MAX</strong> trata los datos personales de remitentes y
                destinatarios en la provisión de servicios de logística y transporte en Lima Metropolitana y Callao.
                Se rige por la <strong>Ley N.° 29733</strong>, su reglamento (D.S. 003-2013-JUS) y normas
                complementarias vigentes.
              </p>
            </motion.div>

            {/* TOC */}
            <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="mt-8 grid gap-2 sm:grid-cols-2">
              {toc.map(([label, id]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={scrollTo(id)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold hover:bg-slate-50"
                >
                  {label}
                </a>
              ))}
            </motion.div>

            {/* Sections */}
            <Section id="objeto" title="1. Objeto y alcance">
              <p className="text-slate-700">
                Aplicable a clientes (remitentes), destinatarios finales y usuarios de nuestra plataforma y canales de
                atención. Cubre operaciones de recogida, transporte, entrega, contraentrega (cobranza en destino),
                seguimiento y conciliación.
              </p>
            </Section>

            <Section id="responsable" title="2. Identidad del responsable">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li><b>Responsable del tratamiento:</b> FAST MAX (la “Compañía”).</li>
                <li><b>Domicilio:</b> Lima, Perú.</li>
                <li><b>Correo de contacto:</b> <a className="underline" href="mailto:contacto@fastmax.pe">contacto@fastmax.pe</a></li>
                <li>
                  <b>Bancos de datos personales:</b> p. ej., “Clientes y Prospectos”, “Logística y Entregas”, “Atención y
                  Mensajería”. <em>(Registrar/actualizar en el RNPDP según corresponda.)</em>
                </li>
              </ul>
            </Section>

            <Section id="bases-finalidades" title="3. Bases legales y finalidades">
              <p className="text-slate-700">
                Tratamos datos bajo las bases de <b>ejecución de contrato/medidas precontractuales</b>,{" "}
                <b>consentimiento</b> (cuando sea requerido), <b>interés legítimo</b> y <b>cumplimiento de obligaciones legales</b>.
                Finalidades principales:
              </p>
              <ul className="pl-4 mt-2 space-y-2 text-slate-700">
                <li>Gestionar envíos (alta de pedidos, ruteo, seguimiento, entrega, incidencias, devoluciones).</li>
                <li>Comunicaciones operativas con remitentes/destinatarios (incl. WhatsApp/SMS/email).</li>
                <li>Conciliaciones y depósitos por contraentrega (COD).</li>
                <li>Prevención de fraude y seguridad de la operación.</li>
                <li>Mejora del servicio y analítica de desempeño (KPIs, tiempos, rechazos, SLA).</li>
                <li>Cumplimiento de obligaciones regulatorias y atención de requerimientos de autoridad.</li>
                <li>Comunicaciones comerciales (opcional y con consentimiento cuando corresponda).</li>
              </ul>
            </Section>

            <Section id="datos" title="4. Datos que tratamos">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Identificación y contacto: nombres, DNI/RUC (cuando aplique), teléfono, email.</li>
                <li>Datos de envío: direcciones, referencias, notas, ventana/fecha, estado, prueba de entrega.</li>
                <li>Datos transaccionales: importes de contraentrega, conciliaciones y depósitos al remitente.</li>
                <li>Soporte y mensajería: contenido esencial de comunicaciones operativas (no sensibles).</li>
                <li>Geodatos operativos: coordenadas del repartidor, ETA y trazas de ruta (no perfilamiento sensible).</li>
                <li>Datos técnicos: logs, IP, dispositivo/navegador (para seguridad y métricas).</li>
              </ul>
              <p className="text-slate-700 mt-2 text-sm">
                <b>Datos sensibles/menores:</b> no solicitamos datos sensibles ni tratamos datos de menores sin autorización válida.
              </p>
            </Section>

            <Section id="fuentes" title="5. Fuentes de datos">
              <p className="text-slate-700">
                El cliente carga pedidos (p. ej., Excel/CSV) o usa integraciones manuales. También recibimos datos
                generados por la operación (tracking, eventos, mensajería) y datos provistos por el destinatario al coordinar la entrega.
              </p>
            </Section>

            <Section id="whatsapp" title="6. Mensajería (WhatsApp) y comunicaciones">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Usamos API oficial de WhatsApp/SMS/email para notificar hitos de entrega y coordinar incidencias.</li>
                <li>El cliente es responsable de la <b>licitud</b> de los datos de sus destinatarios y de informarles sobre estas comunicaciones.</li>
                <li>Los proveedores de mensajería pueden tratar datos fuera del Perú. Ver “Transferencias internacionales”.</li>
                <li>El destinatario puede desuscribirse de comunicaciones comerciales; las <b>operativas</b> son necesarias para el servicio.</li>
              </ul>
            </Section>

            <Section id="geo" title="7. Geolocalización y seguimiento">
              <p className="text-slate-700">
                Para brindar seguimiento en tiempo real, tratamos geolocalización de conductores y eventos de ruta.
                Estos datos se limitan a fines operativos, seguridad y mejora del servicio.
              </p>
            </Section>

            <Section id="cookies" title="8. Cookies y tecnologías similares">
              <p className="text-slate-700">
                Usamos cookies esenciales (seguridad, sesión) y, con consentimiento cuando aplique, cookies analíticas o
                de rendimiento para medir uso de la plataforma. Puedes gestionar preferencias desde tu navegador o un
                banner de cookies (si está habilitado).
              </p>
            </Section>

            <Section id="plazos" title="9. Conservación (plazos)">
              <p className="text-slate-700">
                Conservamos datos por el tiempo necesario para las finalidades descritas y para cumplir obligaciones
                legales/contractuales. Luego se eliminarán o anonimizarán de forma segura. Ejemplos orientativos:
              </p>
              <ul className="pl-4 mt-2 space-y-2 text-slate-700">
                <li>Operación de envíos y conciliaciones: durante la relación y hasta <b>X</b> años posteriores.</li>
                <li>Logs de seguridad: <b>X</b> meses.</li>
                <li>Mensajería operativa: según límites técnicos y legales del proveedor (<em>retención mínima necesaria</em>).</li>
              </ul>
            </Section>

            <Section id="encargados" title="10. Encargados, destinatarios y transferencias">
              <p className="text-slate-700">
                Podemos compartir datos con <b>encargados de tratamiento</b> (proveedores) bajo contratos que imponen
                medidas de seguridad y finalidades limitadas: mensajería, cloud/hosting, analítica, soporte, pagos/finanzas,
                verificación antifraude, atención al cliente y logística de devolución. También con autoridades cuando sea
                legalmente requerido.
              </p>
            </Section>

            <Section id="transferencias" title="11. Transferencias internacionales">
              <p className="text-slate-700">
                Si un proveedor procesa datos fuera del Perú (p. ej., servicios cloud o API de mensajería), FAST MAX
                adoptará salvaguardas adecuadas para proteger tus datos (cláusulas contractuales, estándares de seguridad
                y obligaciones de confidencialidad), conforme a la normativa peruana.
              </p>
            </Section>

            <Section id="seguridad" title="12. Seguridad de la información">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Controles de acceso, cifrado en tránsito, registro de eventos y segmentación por roles.</li>
                <li>Políticas y procedimientos para gestión de incidentes y continuidad.</li>
                <li>Evaluaciones periódicas y deber de confidencialidad con el personal y proveedores.</li>
              </ul>
            </Section>

            <Section id="arco" title="13. Derechos ARCO y revocación">
              <p className="text-slate-700">
                Puedes ejercer tus derechos de <b>Acceso, Rectificación, Cancelación y Oposición</b> (ARCO), así como
                la <b>revocación de consentimiento</b> cuando corresponda:
              </p>
              <ul className="pl-4 mt-2 space-y-2 text-slate-700">
                <li>
                  Envíanos tu solicitud a <a className="underline" href="mailto:contacto@fastmax.pe">contacto@fastmax.pe</a> indicando:
                  (i) nombres y documento de identidad, (ii) petición concreta, (iii) correo para notificaciones.
                </li>
                <li>
                  Plazos orientativos conforme a ley: <b>20 días hábiles</b> para acceso; <b>10 días hábiles</b> para
                  rectificación/cancelación/oposición, a partir de la recepción completa de la solicitud.
                </li>
                <li>
                  Si consideras que no hemos atendido adecuadamente tu solicitud, puedes acudir a la Autoridad Nacional
                  de Protección de Datos Personales.
                </li>
              </ul>
            </Section>

            <Section id="menores" title="14. Menores de edad">
              <p className="text-slate-700">
                Nuestros servicios están dirigidos a empresas/comercios. No tratamos datos de menores de forma
                intencional. Si detectas un tratamiento indebido, escríbenos para gestionar la eliminación oportuna.
              </p>
            </Section>

            <Section id="cambios" title="15. Cambios a esta política">
              <p className="text-slate-700">
                Podemos actualizar esta política por cambios legales u operativos. Publicaremos la versión vigente en la
                plataforma y, cuando corresponda, te informaremos por medios electrónicos.
              </p>
            </Section>

            <Section id="contacto" title="16. Contacto y canal de privacidad">
              <ul className="pl-4 space-y-2 text-slate-700">
                <li>Correo: <a className="underline" href="mailto:contacto@fastmax.pe">contacto@fastmax.pe</a></li>
                <li>Domicilio: Lima, Perú</li>
                <li>Asunto sugerido: “Solicitud ARCO / Privacidad”.</li>
              </ul>
              <p className="text-xs text-slate-500 mt-2">
                * Esta política es una guía operativa y debe ser validada legalmente para reflejar fielmente tus bancos de datos,
                registros en RNPDP, plazos reales y proveedores usados.
              </p>
            </Section>

            {/* Back CTA */}
            <div className="mt-10 flex justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm"
                style={{ background: brand.primary }}
              >
                Volver al inicio
                <ChevronRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ---------- Small Section helper ---------- */
function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <motion.h2
        {...fadeUp}
        className="mt-10 text-xl font-extrabold"
        style={{ color: brand.primary }}
      >
        {title}
      </motion.h2>
      <motion.div {...fadeUp} transition={{ delay: 0.02 }} className="mt-3">
        {children}
      </motion.div>
    </section>
  );
}
